import { useDeepEqualValue } from '@superdispatch/hooks';
import { HTTPEndpointTemplate } from '@superdispatch/http';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useAPIListQuery } from 'shared/api/APIListQuery';
import { APIPageQueryOptions, useAPIPageQuery } from 'shared/api/APIPageQuery';
import { APIQueryResult, useAPIQuery } from 'shared/api/APIQuery';
import { APIPageResponse } from 'shared/api/CarrierAPIClient';
import {
  LoadboardAPIPageResponse,
  LoadboardAPIResponse,
  mapLoadboardAPIPageToCarrierAPIPage,
  mapLoadboardAPIToCarrierAPI,
  requestJSONLoadboardAPI,
  requestLoadboardAPI,
} from 'shared/api/LoadboardAPIClient';
import { THREE_SECONDS } from 'shared/constants/NumberConstants';
import { getUTMTag } from 'shared/helpers/Analytics';
import {
  createSearchCriteria,
  PostingSearchCriteriaDTO,
  postingSearchCriteriaPayload,
  searchCriteriaToJSON,
} from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import {
  PostingCarrierLoadDTO,
  postingCarrierLoadSchema,
  PostingDTO,
  postingSchema,
} from './dto/PostingDTO';
import { PostingRequestPayloadDTO } from './dto/PostingRequestDTO';
import { normalizeSearchCriteria } from './dto/PostingSavedSearchDTO';
import {
  isEmptyCriteria as isEmptyCriteriaData,
  PostingsPageParamsDTO,
  PostingsPageTab,
} from './dto/PostingsPageParamsDTO';
import { fetchSavedLoads } from './LoadboardSavedLoadsAPI';
import {
  clearPostingCache,
  populatePostingFromCache,
  setPostingCache,
} from './PostingLocalCache';

function sortText(value: string) {
  return value
    .split('')
    .sort((a, b) => a.localeCompare(b))
    .join('');
}

function isSearchCriteriaEqual(
  a: PostingSearchCriteriaDTO,
  b: PostingSearchCriteriaDTO,
): boolean {
  const json1 = searchCriteriaToJSON(a);
  const json2 = searchCriteriaToJSON(b);
  return !!json1 && !!json2 && sortText(json1) === sortText(json2);
}

const USER_TRIGGERED_SEARCHES: PostingSearchCriteriaDTO[] = [];
export function markSearchAsUserTriggered(
  criteria: PostingSearchCriteriaDTO,
): PostingSearchCriteriaDTO {
  USER_TRIGGERED_SEARCHES.push(createSearchCriteria(criteria));
  return criteria;
}

function detectUserTriggerSearchHash(value?: PostingSearchCriteriaDTO) {
  const criteria = createSearchCriteria(value);
  const index = USER_TRIGGERED_SEARCHES.findIndex((search) =>
    isSearchCriteriaEqual(search, criteria),
  );

  if (index !== -1) {
    USER_TRIGGERED_SEARCHES.splice(index, 1);
    return true;
  }

  return false;
}

function searchPostings(
  page: number,
  size: number,
  sort: string | string[],
  criteria?: PostingSearchCriteriaDTO,
): Promise<APIPageResponse<PostingDTO>> {
  const isUserInitiated = detectUserTriggerSearchHash(criteria);

  return requestJSONLoadboardAPI<LoadboardAPIPageResponse<PostingDTO>>(
    [
      'POST /internal/v3/loads/search{?page,size,sort*,is_user_initiated}',
      { page, size, sort, is_user_initiated: isUserInitiated },
    ],
    {
      json: criteria
        ? postingSearchCriteriaPayload.cast(normalizeSearchCriteria(criteria))
        : {},
    },
  ).then((response) => {
    return mapLoadboardAPIPageToCarrierAPIPage(response);
  });
}

function getRequests(
  page: number,
  size: number,
): Promise<LoadboardAPIPageResponse<PostingDTO>> {
  return requestJSONLoadboardAPI<LoadboardAPIPageResponse<PostingDTO>>([
    'GET /internal/v3/requests{?page,size}',
    { page, size },
  ]);
}

function getBookings(
  page: number,
  size: number,
): Promise<LoadboardAPIPageResponse<PostingDTO>> {
  return requestJSONLoadboardAPI<LoadboardAPIPageResponse<PostingDTO>>([
    'GET /internal/v3/bookings{?page,size}',
    { page, size },
  ]);
}

function getSuggestedLoads(
  page: number,
  size: number,
): Promise<LoadboardAPIPageResponse<PostingDTO>> {
  return requestJSONLoadboardAPI<LoadboardAPIPageResponse<PostingDTO>>([
    'GET /internal/v3/loads/suggestions/all{?page,size}',
    { page, size },
  ]);
}

// The scroll key in usePostingsListScrollKey is built from this, so that a
// change to the query a carrier is looking at always re-baselines the new loads
// detector. Anything added here that is left out there makes a fresh result set
// read as arrivals.
export function getPostingListIdentity(params: PostingsPageParamsDTO) {
  return {
    criteria: params.search_criteria,
    currentPage: params.current_page,
    size: params.current_page_size,
    sort: params.page_sort,
    tab: params.current_tab,
  };
}

export function usePostingList(params: PostingsPageParamsDTO) {
  const queryClient = useQueryClient();
  const pureSearchCriteria = useDeepEqualValue(params.search_criteria);

  const postings = useAPIPageQuery(
    [
      'postings',
      'list',
      { ...getPostingListIdentity(params), criteria: pureSearchCriteria },
    ],
    () => {
      if (params.current_tab === 'saved-search') {
        throw new Error('Invalid current_tab');
      }

      if (params.current_tab === 'private-loadboard') {
        return searchPostings(
          params.current_page,
          params.current_page_size,
          params.page_sort,
          {
            ...pureSearchCriteria,
            posted_to_private_loadboard: true,
          },
        );
      }

      if (!isEmptyCriteriaData(params)) {
        return searchPostings(
          params.current_page,
          params.current_page_size,
          params.page_sort,
          pureSearchCriteria,
        );
      }

      if (params.current_tab === 'saved-loads') {
        return fetchSavedLoads(
          params.current_page,
          params.current_page_size,
        ).then(mapLoadboardAPIPageToCarrierAPIPage);
      }

      if (params.current_tab === 'suggested') {
        return getSuggestedLoads(
          params.current_page,
          params.current_page_size,
        ).then(mapLoadboardAPIPageToCarrierAPIPage);
      }

      if (params.current_tab === 'booked') {
        return getBookings(params.current_page, params.current_page_size).then(
          mapLoadboardAPIPageToCarrierAPIPage,
        );
      }

      if (params.current_tab === 'requested') {
        return getRequests(params.current_page, params.current_page_size).then(
          mapLoadboardAPIPageToCarrierAPIPage,
        );
      }

      return searchPostings(
        params.current_page,
        params.current_page_size,
        params.page_sort,
      );
    },
    {
      enabled: params.current_tab !== 'saved-search',
      schema: postingSchema,
      staleTime: THREE_SECONDS,
    },
  );

  useEffect(() => {
    if (postings.data) {
      clearPostingCache();
      for (const item of postings.data.data) {
        setPostingCache(item);
        queryClient.setQueryData(
          ['postings', 'item', { guid: item.load.guid }],
          item,
        );
      }
    }
  }, [postings.data, queryClient]);

  return postings;
}

interface SearchPostingsListOptions {
  // We want to cache search results only from searches triggered by user
  shouldCacheSearchResults?: boolean;
}

export function useSearchPostingsList(
  params: Pick<
    PostingsPageParamsDTO,
    'search_criteria' | 'current_page_size' | 'page_sort'
  >,
  { shouldCacheSearchResults = true }: SearchPostingsListOptions = {},
) {
  const queryClient = useQueryClient();
  const postingsList = useAPIListQuery(
    ['postings', 'list-infinite', { params }],
    (page) => {
      return searchPostings(
        page,
        params.current_page_size,
        params.page_sort,
        params.search_criteria,
      );
    },
    { schema: postingSchema, initialPageParam: 0 },
  );

  useEffect(() => {
    if (!shouldCacheSearchResults || !postingsList?.data?.pages) {
      return;
    }

    postingsList.data.pages
      .flatMap((res) => res.data)
      .forEach((item) => {
        setPostingCache(item);
        queryClient.setQueryData(
          ['postings', 'item', { guid: item.load.guid }],
          item,
        );
      });
  }, [postingsList?.data?.pages, queryClient, shouldCacheSearchResults]);

  return postingsList;
}

export interface PostingsCount {
  available: number;
  saved: number;
  booked: number;
  requested: number;
  suggested: number;
}

export function usePostingsCount() {
  return useQuery({
    queryKey: ['loadboard', 'loads-count'],
    queryFn: () =>
      requestJSONLoadboardAPI<LoadboardAPIResponse<PostingsCount>>(
        'GET /internal/v1/statistics/loads',
      ).then((response) => response.data),
  });
}

export interface PrivateLoadboardCount {
  total_loads_count: number;
  shippers: Array<{
    guid: string;
    name: string;
    logo_url: string;
    loads_count: number;
  }>;
}

export function usePrivateLoadboardCount() {
  return useAPIQuery<PrivateLoadboardCount>(
    ['loadboard', 'private-loadboard-count'],
    () =>
      requestJSONLoadboardAPI(
        'GET /internal/v3/loads/private-network/count-by-shipper',
      ),
  );
}

export function usePosting(
  guid: string | undefined,
  tab: PostingsPageTab,
): APIQueryResult<PostingDTO> {
  const url: HTTPEndpointTemplate =
    tab === 'booked'
      ? 'GET /internal/v3/bookings/{guid}'
      : tab === 'requested'
        ? 'GET /internal/v3/requests/{guid}/with_load'
        : 'GET /internal/v3/loads/{guid}';
  return useAPIQuery(
    ['postings', 'item', { guid }],
    () =>
      requestJSONLoadboardAPI<LoadboardAPIResponse<PostingDTO>>([url, { guid }])
        .then((response) => {
          populatePostingFromCache(response.data);
          return response;
        })
        .then(mapLoadboardAPIToCarrierAPI),
    { enabled: !!guid, schema: postingSchema },
  );
}

function invalidatePosting(queryClient: QueryClient, guid: string) {
  void queryClient.invalidateQueries({
    queryKey: ['postings', 'item', { guid }],
  });
}

export function useSuggestedLoads(
  params?: { page?: number; size?: number; sort?: string },
  options?: APIPageQueryOptions<PostingDTO>,
) {
  const queryClient = useQueryClient();
  const suggestedLoads = useAPIPageQuery(
    ['postings', 'list', { tab: 'suggested', ...params }],
    () =>
      requestJSONLoadboardAPI<LoadboardAPIPageResponse<PostingDTO>>([
        'GET /internal/v3/loads/suggestions/all{?page,size}',
        { params },
      ]).then(mapLoadboardAPIPageToCarrierAPIPage),
    { ...options, schema: postingSchema },
  );

  useEffect(() => {
    if (suggestedLoads?.data?.data) {
      for (const item of suggestedLoads?.data.data) {
        queryClient.setQueryData(
          ['postings', 'item', { guid: item.load.guid }],
          item,
        );
      }
    }
  }, [suggestedLoads?.data?.data, queryClient]);

  return suggestedLoads;
}

function usePostingsCache() {
  const queryClient = useQueryClient();

  return useMemo(() => {
    function invalidatePostingsCount() {
      void queryClient.invalidateQueries({
        queryKey: ['loadboard', 'loads-count'],
        type: 'all',
      });
    }

    function invalidatePostingsList() {
      void queryClient.invalidateQueries({ queryKey: ['postings', 'list'] });
    }

    function invalidatePostings() {
      invalidatePostingsCount();
      invalidatePostingsList();
    }

    return { invalidatePostings };
  }, [queryClient]);
}

export function usePostingAPI() {
  const queryClient = useQueryClient();
  const { invalidatePostings } = usePostingsCache();

  return useMemo(
    () => ({
      bookPosting: (
        loadGuid: string,
        json: PostingRequestPayloadDTO,
      ): Promise<PostingCarrierLoadDTO> =>
        requestJSONLoadboardAPI(
          ['POST /internal/v3/bookings/{loadGuid}', { loadGuid }],
          {
            json,
          },
        ).then(({ data }) => {
          invalidatePostings();
          return postingCarrierLoadSchema.cast(data);
        }),

      requestPosting: (
        loadGuid: string,
        json: PostingRequestPayloadDTO,
      ): Promise<PostingCarrierLoadDTO> =>
        requestJSONLoadboardAPI<LoadboardAPIResponse<PostingCarrierLoadDTO>>(
          ['POST /internal/v3/requests/{loadGuid}', { loadGuid }],
          { json },
        ).then(({ data }) => {
          invalidatePostings();
          return postingCarrierLoadSchema.cast(data);
        }),

      updatePostingRequest: (
        loadGuid: string,
        requestGUID: string,
        json: PostingRequestPayloadDTO,
      ) =>
        requestJSONLoadboardAPI<LoadboardAPIResponse<PostingCarrierLoadDTO>>(
          ['PUT /internal/v3/requests/{requestGUID}', { requestGUID }],
          { json },
        ).then(({ data }) => {
          invalidatePosting(queryClient, loadGuid);
          invalidatePostings();
          return data;
        }),
      cancelPostingRequest: (requestGUID: string) =>
        requestLoadboardAPI([
          'DELETE /internal/v3/requests/{requestGUID}',
          { requestGUID },
        ]).then((response) => {
          invalidatePostings();
          return response;
        }),
      trackLoad: (loadGuid: string, postingGuid: string) => {
        let action = getUTMTag('utm_medium');
        let source = getUTMTag('utm_source');

        // Android app overrides SMS source to "Mobile CTMS" or ["SMS", "Mobile CTMS"]
        if (String(source).includes('Mobile CTMS')) {
          source = 'SMS';
          action ||= 'Load Alert';
        }

        return requestLoadboardAPI([
          'POST /internal/v3/loads/{loadGuid}/track{?posting_guid,action,source}',
          { loadGuid, posting_guid: postingGuid, action, source },
        ]).catch(() => {}); // handleError already logs them via logWarning
      },
    }),
    [queryClient, invalidatePostings],
  );
}
